<template>
  <div :class="getItemTableContainerClass">
    <ItemTable
      ref="itemTable"
      :boundItemsUrl="boundItemsUrl"
      :collectionFilters="collectionFilters"
      :columns="columns"
      :details="inMainPanel"
      :disableGettingStarted="showHrDirectoryEmptyState"
      :gettingStartedItemType="itemType"
      :handleDetailsButtonClick="handleDetailsButtonClick"
      :handleMainAction="handleMainAction"
      :handleRowClick="handleRowClick"
      :handleUnselectLockedItem="handleUnselectLockedDirectoryItem"
      :handleUnselectMultipleLockedItems="handleUnselectMultipleDirectoryLockedItems"
      :hasSelectColumn="hasSelectColumn"
      :inDetailsAsidePanel="inDetailsAsidePanel"
      :isAsideOpen="isAsideOpen"
      :isItemLocked="isDirectoryItemLocked"
      :itemType="itemTableType"
      :keepFresh="inMainPanel"
      :mainActionMenuItems="mainActionMenuItems"
      :reportType="reportType"
      :selectionItemText="{
        label: inMainPanel ? 'selected' : 'bound',
        plural: 'directories',
        singular: 'directory'
      }"
      :setGettingStartedTooltipVisibility="setGettingStartedTooltipVisibility"
      :showBound="false"
      :showMainAction="inMainPanel"
      :showSearchForm="false"
    >
      <template
        v-if="showHrDirectoryEmptyState"
        v-slot:emptyState
      >
        <HRDirectoriesEmptyState />
      </template>
    </ItemTable>
    <Modal
      v-model="modalVisible.ldapBindingUser"
      cancelText="cancel"
      :handleMainAction="handleUnbindLdapBindingUser"
      headerText="LDAP Bind DN"
      mainActionText="continue"
    >
      <p>
        This user has been enabled as an LDAP Bind DN. Removing them from the JumpCloud
        LDAP Directory will also remove their Bind DN privileges.
      </p>
    </Modal>
    <Modal
      v-model="modalVisible.unableToRemoveUser"
      cancelText=""
      :handleMainAction="() => closeModal(modalTypes.unableToRemoveUser)"
      headerText="Unable to Remove User"
      mainActionText="OK"
    >
      <p>
        This user is the active Samba Service Account. To remove them from LDAP, you
        must designate another Samba Service Account or disable Samba authentication
        in the JumpCloud LDAP Configuration.
      </p>
    </Modal>
    <Modal
      v-model="modalVisible.unableToRemoveActiveDirectory"
      cancelText=""
      :handleMainAction="() => closeModal(modalTypes.unableToRemoveActiveDirectory)"
      headerText="Unable to Remove Active Directory Domain(s)"
      mainActionText="OK"
    >
      <p>
        Users that are bound to an Active Directory Domain must be removed from the domain on the
        Active Directory Server.
      </p>
    </Modal>

    <ConfiguredAppsDetectedModal
      v-if="showConfiguredAppsDetectedModal"
      :appDisplayName="currentlySelectedAppDisplayName"
      :applications="previouslyConfiguredApps"
      @close="handleAppsDetectedModalClose"
      @continue="handleAppsDetectedModalContinue"
    />
  </div>
</template>

<script>
import {
  DirectoryAppsTemplatesNames,
  DirectoryNameConsts,
  DirectoryTypeConsts,
  DirectoryTypeKeys,
} from 'client/util/Constants/DirectoryConstants';
import {
  DirectoryGroupingConsts,
  ItemTableConsts,
  ItemTypeConsts,
  StoreModuleMap,
} from 'client/util/Constants';
import { ReportTypes } from 'client/Reports/ReportMetadata';
import { events } from 'client/GrowthEngineering/utilities/GrowthConstants';
import {
  find,
  noop,
  values,
} from 'lodash';
import { mapActions, mapGetters } from 'vuex';

import { getAppTemplateRoute } from 'client/applications/utils/ApplicationUtils';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import BambooIcon from 'public/img/icons/bamboo-hr.svg?inline';
import ConfiguredAppsDetectedMixin from 'client/directories/components/ConfiguredAppsDetectedMixin';
import ConfiguredAppsDetectedModal from 'client/directories/components/ConfiguredAppsDetectedModal.vue';
import CreateAdDomainModal from 'client/directories/ActiveDirectory/CreateAdDomainModal.vue';
import DirectoryApi from 'client/api/v2/DirectoryApi';
import DirectoryDialogsHelper from 'client/directories/DirectoryDialogsHelper';
import DirectoryItemTableMethodsMixin from 'client/directories/components/DirectoryItemTableMethodsMixin';
import DirectoryTokenStatus from 'client/components/ItemTable/BodyComponents/DirectoryTokenStatus.vue';
import DirectoryTypeIcon from 'client/components/ItemTable/BodyComponents/DirectoryTypeIcon.vue';
import DirectoryUtil from 'client/util/DirectoryUtil';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import EventManager from 'client/GrowthEngineering/EventManager';
import GoogleIcon from 'public/img/icons/google-workspace.svg?inline';
import GustoIcon from 'public/img/icons/icon_directories_gusto.svg?inline';
import HRDirectoriesEmptyState from 'client/directories/components/HRDirectoriesEmptyState.vue';
import HibobIcon from 'public/img/icons/icon_directories_bob.svg?inline';
import ItemTable from 'client/components/ItemTable/ItemTable.vue';
import ItemTableComputedMixin from 'client/components/ItemTable/mixins/ItemTableComputedMixin';
import ItemTableIndirectBindingMixin from 'client/components/ItemTable/mixins/ItemTableIndirectBindingMixin';
import ItemTableMethodsMixin from 'client/components/ItemTable/mixins/ItemTableMethodsMixin';
import ItemTablePropsMixin from 'client/components/ItemTable/mixins/ItemTablePropsMixin';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import LDAPIcon from 'public/img/icons/ldap.svg?inline';
import M365Icon from 'public/img/icons/microsoft365.svg?inline';
import Modal from 'client/components/Modal/Modal.vue';
import ModalEventBus from 'components/Modal/ModalEventBus';
import NamelyIcon from 'public/img/icons/icon_directories_namely.svg?inline';
import OktaIcon from 'public/img/icons/okta.svg?inline';
import PersonioIcon from 'public/img/icons/personio.svg?inline';
import WorkdayIcon from 'public/img/icons/workdayAvatar.svg?inline';

import directoryRouteMixin from 'client/components/mixins/directoryRouteMixin';

const { columnTypes } = ItemTableConsts;
const { groupAsidePanel, mainPanel, userAsidePanel } = ItemTableConsts.panelType;
const directoryCollection = StoreModuleMap[ItemTypeConsts.directory];
const ldapBindingUser = 'ldapBindingUser';
const unableToRemoveActiveDirectory = 'unableToRemoveActiveDirectory';
const unableToRemoveUser = 'unableToRemoveUser';

export default {
  name: 'DirectoryItemTable',

  components: {
    ConfiguredAppsDetectedModal,
    ItemTable,
    HRDirectoriesEmptyState,
    Modal,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
    ConfiguredAppsDetectedMixin,
    DirectoryItemTableMethodsMixin,
    ItemTableComputedMixin,
    ItemTableIndirectBindingMixin,
    ItemTableMethodsMixin,
    ItemTablePropsMixin,
    directoryRouteMixin,
  ],

  DirectoryApi,

  props: {
    itemType: {
      default: ItemTypeConsts.directory,
      type: String,
    },
    onLdapDirectoryUnselected: {
      default: () => {},
      type: Function,
    },
    panelType: {
      default: mainPanel,
      type: String,
      required: false,
    },
  },

  data() {
    let hasSelectColumn = false;

    const collectionFilters = [];
    const { groupTypes, memberTypes } = DirectoryGroupingConsts;
    const { itemType } = this;
    const { activeDirectory, cloudDirectory } = ItemTypeConsts;
    let mainActionMenuItems = [];
    let groupType;
    let memberType;

    if (this.inUserAside()) {
      groupType = groupTypes.userGroup;
      memberType = memberTypes.user;
    }

    if (this.panelType === mainPanel) {
      mainActionMenuItems = this.createMainActionMenuItems();
    }

    const columns = [
      {
        bodyComponent: 'DirectoryTypeIcon',
        classNames: {
          body: [columnTypes.iconColumn],
          heading: [columnTypes.iconColumn],
        },
        headingText: 'Type',
      },
      {
        bodyComponent: 'ItemName',
        bodyComponentProps: {
          subtitleFunction: item => this.subtitleFunction(item),
          titleFunction: item => this.titleFunction(item),
        },
        classNames: {
          body: [this.$style.nameColumn],
        },
        headingText: 'Name',
        sortBy: 'name',
      },
      {
        bodyComponent: DirectoryTokenStatus,
        headingText: this.headingTokenText(),
      },
    ];

    if (this.inDetailsAsidePanel) {
      // Allow directories to be selected in Aside Panels
      hasSelectColumn = true;
    }

    // Show the access configurations column in the User Aside Panel
    if (this.inUserAside()) {
      columns.push({
        bodyComponent: 'DirectoryAccessConfiguration',
        bodyComponentProps: {
          parentType: this.parentType,
        },
        headingText: 'Access Configurations',
      });
    }

    return {
      collectionFilters,
      columns,
      groupType,
      hasSelectColumn,
      itemTableType: ItemTypeConsts.directory,
      mainActionMenuItems,
      memberType,
      modalVisible: {
        [ldapBindingUser]: false,
        [unableToRemoveActiveDirectory]: false,
        [unableToRemoveUser]: false,
      },
      modalTypes: {
        ldapBindingUser,
        unableToRemoveActiveDirectory,
        unableToRemoveUser,
      },
      reportType: itemType === activeDirectory || itemType === cloudDirectory
        ? ReportTypes.userToDirectory
        : null,
    };
  },

  computed: {
    ...mapGetters(directoryCollection, {
      isItemPreviouslySelected: 'isItemPreviouslySelected',
      items: 'getCollection',
      previouslyDirectlyBoundItems: 'getPreviouslyDirectlyBoundItems',
      previouslyDirectlyBoundItemsCount: 'getPreviouslyDirectlyBoundItemsCount',
      selectedItems: 'getSelectedItems',
      selectedItemsCount: 'getSelectedItemsCount',
    }),

    showHrDirectoryEmptyState() {
      return this.itemType === ItemTypeConsts.hrDirectory
        && this.items.length === 0;
    },
  },

  watch: {
    items: {
      handler() {
        this.updateMainActionMenuItems();
      },
      deep: true,
    },
  },

  beforeCreate() {
    const { panelType } = this.$options.propsData;

    if (panelType === groupAsidePanel) {
      this.$options.methods.handleBeforeCreate(this.$options, directoryCollection);
    } else if (panelType === userAsidePanel) {
      this.$options.methods.handleBeforeCreate(this.$options, directoryCollection);
      this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options, true);
    }
  },

  /**
   * When the DirectoryItemTable is rendered in the User or Directory Aside Panel register
   * watchers to check if LDAP was unselected when either the direct binding to an item that is
   * both indirectly and directly bound to the parent is removed, and when a directly bound item is
   * unselected.
   */
  created() {
    if (this.inGroupAside() || this.inUserAside()) {
      this.$watch('previouslyDirectlyBoundItemsCount', this.checkIfLdapDirectBindingWasRemoved);
      this.$watch('selectedItemsCount', this.checkIfLdapWasUnselected);
    }

    if (this.panelType === mainPanel && this.itemType === ItemTypeConsts.hrDirectory) {
      this.setUpAppTemplatesHref(DirectoryAppsTemplatesNames);
    }
  },

  beforeMount() {
    this.setCollectionFilters();
  },

  methods: {
    ...mapActions(directoryCollection, [
      'selectItem',
      'unselectItem',
    ]),

    ...mapGetters(directoryCollection, [
      'getItem',
      'getLdap',
      'isActiveDirectory',
      'isLdap',
    ]),

    /**
     * The main action menu items are created when the DirectoryItemTable's data
     * is initialized. The menu items will then be updated when the items are
     * available so that single instance directories are _disabled_ in the menu.
     * @todo
     * 1. Extend the Active Directory logic, to navigate directly to the Workday Directory Aside
     *    Panel, when showing HR Directories until there is more than one of them.
     * 2. Don't display Active Directory and Workday in the dropdown when displaying Cloud
     *    Directories in the <DirectoryItemTable>.
     */
    createMainActionMenuItems() {
      if (this.itemType === ItemTypeConsts.activeDirectory) {
        // The DirectoryItemTable is used to display only Active Directory domain instances on the
        // #/directories/active-directory page. So on that page, instead of displaying a dropdown
        // of directories to create, the Create New Active Directory Domain dialog is displayed
        // directly instead.
        return [];
      }

      if (this.itemType === ItemTypeConsts.hrDirectory) {
        /**
       * CO-78
       *
       * The const for DirectoryNameConsts.workday is "Workday Import" which would clash with
       * the "Import from" verbiage we're switching to. So, split off the " Import" from the const.
       */
        const createWorkdayMenuItem = (type = DirectoryTypeConsts.workday) => ({
          clickHandler: () => this.navigateToDirectoryAside(type),
          component: WorkdayIcon,
          disabled: false,
          href: this.directoryTypeAsideRoute(type),
          smallText: '',
          text: DirectoryUtil.getDirectoryImportMenuItemText(DirectoryNameConsts[type].split(' ')[0]),
          type,
        });

        const menuItems = [
          {
            component: HibobIcon,
            componentProps: {
              style: {
                position: 'relative',
              },
            },
            disabled: false,
            external: true,
            href: KbArticleUrls.integratingWithHiBob,
            showExternalLinkIcon: true,
            smallText: '',
            text: 'Real-time sync from bob',
            type: DirectoryTypeKeys.bob,
          },
          {
            component: GustoIcon,
            componentProps: {
              style: {
                position: 'relative',
              },
            },
            disabled: false,
            external: true,
            href: KbArticleUrls.integratingWithGusto,
            showExternalLinkIcon: true,
            smallText: '',
            text: 'Gusto',
            type: DirectoryTypeKeys.gusto,
          },
          {
            component: NamelyIcon,
            componentProps: {
              style: {
                position: 'relative',
              },
            },
            disabled: false,
            external: false,
            href: '',
            smallText: '',
            text: 'Namely',
            type: DirectoryTypeKeys.namely,
          },
          {
            component: PersonioIcon,
            componentProps: {
              style: {
                position: 'relative',
              },
            },
            disabled: false,
            external: false,
            href: '',
            smallText: '',
            text: 'Personio',
            type: DirectoryTypeKeys.personio,
          },
        ];

        if (this.$store.getters['FeatureFlagsModule/betaFeatures'].bambooRealTimeImport) {
          const type = DirectoryTypeConsts.bambooHR;
          const bambooHRMenuItem = {
            component: BambooIcon,
            disabled: false,
            external: true,
            href: KbArticleUrls.bambooHrIntegration,
            showExternalLinkIcon: true,
            smallText: '',
            text: DirectoryUtil.getDirectoryImportMenuItemText(DirectoryNameConsts[type], true),
            type,
          };

          menuItems.unshift(bambooHRMenuItem);
        }

        menuItems.push(createWorkdayMenuItem());

        return menuItems;
      }

      const createGSuiteMenuItem = (type = DirectoryTypeConsts.gSuite) => ({
        clickHandler: () => DirectoryDialogsHelper.showCreateGSuiteDirectoryDialog(),
        component: GoogleIcon,
        smallText: '',
        text: DirectoryUtil.getDirectoryImportMenuItemText(DirectoryNameConsts[type]),
        type,
      });

      if (this.itemType === ItemTypeConsts.cloudDirectory) {
        const createOffice365MenuItem = (type = DirectoryTypeConsts.office365) => ({
          clickHandler: () => DirectoryDialogsHelper.showCreateO365DirectoryDialog(),
          component: M365Icon,
          smallText: '',
          href: this.directoryTypeAsideRoute(type),
          text: DirectoryUtil.getDirectoryImportMenuItemText(DirectoryNameConsts[type]),
          type,
        });

        const createRTImportFromOktaMenuItem = (type = DirectoryTypeConsts.okta) => ({
          component: OktaIcon,
          disabled: false,
          external: true,
          href: KbArticleUrls.oktaIntegration,
          showExternalLinkIcon: true,
          smallText: '',
          text: DirectoryUtil.getDirectoryImportMenuItemText(DirectoryNameConsts[type], true),
          type,
        });

        const createImportFromJCToOktaMenuItem = () => ({
          component: LDAPIcon,
          disabled: false,
          external: true,
          href: KbArticleUrls.jcLdapToOkta,
          showExternalLinkIcon: true,
          smallText: '',
          text: `Import ${DirectoryNameConsts[DirectoryTypeConsts.ldapServer]} into ${DirectoryNameConsts[DirectoryTypeConsts.okta]}`,
        });

        return [
          createGSuiteMenuItem(),
          createOffice365MenuItem(),
          createRTImportFromOktaMenuItem(),
          createImportFromJCToOktaMenuItem(),
        ];
      }

      const createDefaultMenuItem = type => ({
        clickHandler: () => this.navigateToDirectoryAside(type),
        component: DirectoryTypeIcon,
        componentProps: {
          item: { type },
          smallIcon: true,
        },
        disabled: false,
        href: this.directoryTypeAsideRoute(type),
        smallText: '',
        text: DirectoryNameConsts[type],
        type,
      });

      // LDAP has its own page so always exclude it from Main Actions
      return values(DirectoryTypeConsts)
        .filter(type => type !== DirectoryTypeKeys.ldapServer)
        .map((type) => {
          if (type === DirectoryTypeConsts.activeDirectory) {
            return {
              clickHandler: () => this.showCreateAdDomainDialog(),
              component: DirectoryTypeIcon,
              componentProps: {
                item: { type },
                smallIcon: true,
              },
              smallText: '',
              text: DirectoryNameConsts[type],
              type,
            };
          }

          if (type === DirectoryTypeConsts.gSuite) {
            return createGSuiteMenuItem(type);
          }

          if (type === DirectoryTypeConsts.office365) {
            return {
              clickHandler: () => DirectoryDialogsHelper.showCreateO365DirectoryDialog(),
              component: DirectoryTypeIcon,
              componentProps: {
                item: { type },
                smallIcon: true,
              },
              smallText: '',
              href: `#/directories/${type}/new`,
              text: DirectoryNameConsts[type],
              type,
            };
          }

          return createDefaultMenuItem(type);
        });
    },

    /**
     * @todo Use getDetailsRoute() when rendering specific item types in the DirectoryItemTable
     */
    handleDetailsButtonClick(itemId) {
      if (this.inMainPanel) {
        const { type, activated } = this.getItem()(itemId);

        if (this.itemType !== ItemTypeConsts.directory) {
          if (activated) {
            this.$router.push(this.directoryTypeAsideRoute(type, itemId)).catch(noop);
          } else {
            this.$router.push(this.directoryTypeAsideRoute(type, null)).catch(noop);
          }
        } else if (activated || type === DirectoryTypeConsts.ldapServer) {
          this.$router.push(`#/directories/${type}/${itemId}/details`).catch(noop);
        } else {
          this.$router.push(`#/directories/${type}/new`).catch(noop);
        }
      }
    },

    /**
     * @todo Open Workday Directory Aside Panel when itemType is HR Directories.
     */
    handleMainAction() {
      const { triggerEvent } = EventManager;
      triggerEvent(events.addNewDirectoryButtonClicked);

      if (this.itemType === ItemTypeConsts.activeDirectory) {
        this.showCreateAdDomainDialog();
      }
    },

    headingTokenText() {
      return this.itemType !== 'active_directory'
        ? 'Token Status'
        : '';
    },

    navigateToDirectoryAside(directoryType) {
      EventHelper.triggerAddNewDirectoryTypeSelected({ resourceName: directoryType });
      const selectedMenuItem = find(
        this.mainActionMenuItems,
        menuItem => menuItem.type === directoryType,
      );
      this.$router.push(selectedMenuItem.href).catch(noop);
    },

    /**
     * The collection filters are set in the beforeMount lifecycle hook because a beta flag is
     * checked to determine which filters to set and the beta flag is not available when the data
     * is initially populated. Collection filters are a prop that is passed to the ItemTable and in
     * turn to the store and Api instance. They are used to determine which directories are
     * filtered out of the GET /api/v2/directories request - based on the panel the table is
     * rendered in or the itemType it is passed.
     */
    setCollectionFilters() {
      if (this.itemType === ItemTypeConsts.cloudDirectory) {
        this.collectionFilters.push({
          name: 'type',
          operator: 'eq',
          value: [ItemTypeConsts.gSuite, ItemTypeConsts.office365].join('|'),
        });
      } else if (this.itemType === ItemTypeConsts.hrDirectory) {
        this.collectionFilters.push({
          name: 'type',
          operator: 'eq',
          value: ItemTypeConsts.workday,
        });
      } else if (this.itemType !== ItemTypeConsts.directory) {
        this.collectionFilters.push({
          name: 'type',
          operator: 'eq',
          value: this.itemType,
        });
      } else if (this.panelType === mainPanel) {
        this.collectionFilters.push({
          name: 'type',
          operator: 'ne',
          value: `${DirectoryTypeConsts.ldapServer}`,
        });

        // Only show activated directories in the Main Panel
        this.collectionFilters.push({
          name: 'activated',
          operator: 'eq',
          value: true,
        });
      } else {
        // Don't show the Workday Import in Aside Panels
        this.collectionFilters.push({
          name: 'type',
          operator: 'ne',
          value: `${DirectoryTypeConsts.workday}`,
        });
      }
    },

    setUpAppTemplatesHref(templatesNames) {
      const createError = (appName, findAppItem) => {
        const error = new Error('no href');
        error.appName = appName;
        error.findAppItem = findAppItem;
        return error;
      };

      const ssoTemplatesPromises = templatesNames.map(async appName => {
        const href = await getAppTemplateRoute(appName);
        const findAppItem = item => item.type === appName;
        if (href !== '') {
          return {
            appName,
            href,
            findAppItem,
          };
        }

        throw createError(appName, findAppItem);
      });

      const handleError = (error) => {
        if (this.mainActionMenuItems.length > 0) {
          const { findAppItem } = error;
          const index = this.mainActionMenuItems.findIndex(findAppItem);

          if (index > -1) {
            this.mainActionMenuItems.splice(index, 1);
          }
        }
      };

      const appsToCheckForExistingConfigurations = new Set([DirectoryTypeKeys.personio]);

      return Promise.allSettled(ssoTemplatesPromises).then((results) => {
        results.forEach(result => {
          const { value: app } = result;

          if (app) {
            const { findAppItem } = app;
            const menuItem = this.mainActionMenuItems.find(findAppItem);

            if (menuItem) {
              if (appsToCheckForExistingConfigurations.has(menuItem.type)) {
                menuItem.clickHandler = () => {
                  /**
                   * `checkForPreviouslyConfiguredAppsByName` is located in
                   * `ConfiguredAppsDetectedMixin.js`, along with all other data
                   * needed for the `ConfiguredAppsDetectedModal` component.
                   *
                   * This method triggers the `ConfiguredAppsDetectedModal` to display
                   * (or redirect the user to this app's configure URL). More info in
                   * the mixin file.
                   */
                  this.checkForPreviouslyConfiguredAppsByName({
                    appName: menuItem.type,
                    configureURL: app.href,
                  });
                };
              } else {
                menuItem.href = app.href;
              }
            } else {
              throw createError(app.appName, findAppItem);
            }
          } else {
            const { reason: error } = result;
            throw error;
          }
        });
      }).catch((error) => {
        handleError(error);
      });
    },

    showCreateAdDomainDialog() {
      EventHelper.triggerAddNewDirectoryTypeSelected({
        resourceName: DirectoryTypeConsts.activeDirectory,
      });
      ModalEventBus.open({ component: CreateAdDomainModal });
    },

    subtitleFunction(item) {
      if (item.type === DirectoryTypeConsts[DirectoryTypeKeys.activeDirectory]) {
        return DirectoryNameConsts[DirectoryTypeConsts.activeDirectory];
      }

      if (item.type === DirectoryTypeConsts[DirectoryTypeKeys.gSuite]) {
        return DirectoryNameConsts[DirectoryTypeConsts.gSuite];
      }

      if (item.type === DirectoryTypeConsts[DirectoryTypeKeys.office365]) {
        return DirectoryNameConsts[DirectoryTypeConsts.office365];
      }
      return '';
    },

    titleFunction(item) {
      return item.name;
    },

    /**
     * When the items are updated then update the main action menu items:
     *
     * - Use the LDAP server ID in the LDAP menu item URL
     * - Disable the single instance limit directories if found in the items
     */
    updateMainActionMenuItems() {
      if (!this.mainActionMenuItems || this.mainActionMenuItems.length === 0) {
        return;
      }

      const {
        workday,
      } = DirectoryTypeKeys;

      const findItem = (items, type) => find(items, item => item.type === type);

      const menuItemsToUpdate = [workday];

      // If the single instance directories are available in the items then
      // disable them in the main action menu items. And if the single instance
      // items are not available in the items then enable them!
      menuItemsToUpdate.forEach((type) => {
        const menuItem = findItem(this.mainActionMenuItems, DirectoryTypeConsts[type]);
        const item = findItem(this.items, DirectoryTypeConsts[type]);

        // The menu item many not be found. This is the case when displaying directories on the new
        // #/directories/cloud-directories page. In this case just return, the single instance
        // directory is not present.
        if (!menuItem) {
          return;
        }

        // Update menuItem URLs with respect to any computed properties which are now
        // present on the component instance.
        menuItem.href = this.directoryTypeAsideRoute(type, item && item.id);

        if (item && !menuItem.disabled) {
          menuItem.disabled = true;
          menuItem.smallText = '(single instance limit)';
        } else if (!item && menuItem.disabled) {
          menuItem.disabled = false;
          menuItem.smallText = '';
        }
      });
    },
  },
};
</script>

<style module>
.nameColumn {
  max-width: 350px;
}
</style>
